<template>
  <div class="task-list">
    <div v-for="group in taskGroups" :key="group.type" class="task-group">
      <div class="task-group-header">
        <div class="task-group-title">
          <span class="task-group-name">{{ group.label }}</span>
          <span class="task-group-count">{{ group.tasks.length }}</span>
        </div>
        <button
          v-if="group.tasks.length > defaultVisibleCount"
          type="button"
          class="task-group-toggle"
          @click="toggleTaskGroup(group.type)"
        >
          {{ isTaskTypeExpanded(group.type) ? '收起' : '展开全部' }}
        </button>
      </div>

      <div
        v-for="(task, index) in getVisibleTasks(group)"
        :key="getTaskKey(task, index)"
        class="task-item"
      >
        <div class="task-row">
          <div class="left-column">
            <div class="task-type" :class="task.event_type">
              {{ getTaskTypeLabel(task.event_type) }}
            </div>
          </div>

          <div class="task-content">
            <div class="title-wrapper">
              <div class="task-title" @click="previewTask(task)">{{ task.campaign_event_name }}</div>
              <div class="task-meta">
                <!-- <p v-if="task.campaign_name" class="strategy">活动：{{ task.campaign_name }}</p> -->
                <p v-if="task.event_type === 'conference'" class="strategy">会议时间：{{ task.time }}</p>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <img
              v-if="task.event_type === 'conference'"
              :src="require('@/assets/lundbeck/NBA/invite.png')"
              alt="邀请"
              @click.stop="sendTask(task)"
            />
            <img
              v-if="task.event_type === 'content'"
              :src="require('@/assets/lundbeck/NBA/forward.png')"
              alt="发送"
              @click.stop="sendTask(task)"
            />
            <img
              v-if="task.event_type === 'segment' || task.event_type === 'survey'"
              :src="require('@/assets/lundbeck/NBA/fill-form.png')"
              alt="问卷"
              @click.stop="openTypingDialog(task)"
            />
            <img
              src="@/assets/lundbeck/NBA/ignore.png"
              alt="忽略"
              @click.stop="ignoreTask(task)"
            />
          </div>
        </div>
      </div>
    </div>
    <transition name="slide-right">
      <swipeDialog
        v-if="htmlContent"
        backtxt="返回"
        :title="'内容预览'"
        @closeFn="closePreviewFn"
      >
        <div v-if="htmlContent" class="html-preview" v-html="htmlContent"></div>
      </swipeDialog>
    </transition>

    <!-- 问卷弹窗 -->
    <BrintellixTypingDialog
      :show="showTypingDialog"
      :physician-id="typingDialogTask ? typingDialogTask.physician_id : ''"
      :physician-name="typingDialogTask ? typingDialogTask.physician_name : ''"
      :product-id="typingDialogTask ? typingDialogTask.product_id : ''"
      :initial-answers="typingDialogAnswers"
      @update:show="showTypingDialog = $event"
      @close="showTypingDialog = false"
      @finish="onTypingFinish"
    />
  </div>
</template>

<script>
import { Dialog } from 'vant';
import { wecomSDK } from '@/utils/wecom';
import { getContentById } from '@/api/content';
import { getLocalStorage } from "@/utils/index";
import { executeTask, reExecuteTask } from '@/api/nba';
import swipeDialog from '@/views/layout/swipeDialog';
import BrintellixTypingDialog from './BrintellixTypingDialog.vue';
import { selectRecommendedWording } from '@/utils/recommendedWording';

export default {
  name: 'TaskList',
  components: {
    swipeDialog,
    BrintellixTypingDialog,
  },
  props: {
    tasks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      htmlContent: null,
      showTypingDialog: false,
      typingDialogTask: null,
      typingDialogAnswers: null,
      defaultVisibleCount: 5,
      expandedTaskTypes: {},
    };
  },
  computed: {
    taskGroups() {
      const groups = [];
      const groupMap = {};

      this.tasks.forEach((task) => {
        const type = this.getTaskTypeKey(task.event_type);
        if (!groupMap[type]) {
          groupMap[type] = {
            type,
            label: this.getTaskTypeLabel(task.event_type),
            tasks: [],
          };
          groups.push(groupMap[type]);
        }
        groupMap[type].tasks.push(task);
      });

      return groups;
    },
  },
  methods: {
    getTaskTypeKey(eventType) {
      if (eventType === 'conference') {
        return 'conference';
      }
      if (eventType === 'segment' || eventType === 'survey') {
        return 'survey';
      }
      return 'content';
    },
    getTaskTypeLabel(eventType) {
      if (eventType === 'conference') {
        return '直播';
      }
      if (eventType === 'segment' || eventType === 'survey') {
        return '问卷';
      }
      return '推文';
    },
    getTaskKey(task, index) {
      const taskKey = [
        task.campaign_event_execution_id,
        task.campaign_event_id,
        task.physician_id,
        task.content_id,
      ].filter(Boolean).join('_');
      return `${taskKey}_${index}`;
    },
    isTaskTypeExpanded(type) {
      return !!this.expandedTaskTypes[type];
    },
    toggleTaskGroup(type) {
      this.$set(this.expandedTaskTypes, type, !this.expandedTaskTypes[type]);
    },
    getVisibleTasks(group) {
      if (this.isTaskTypeExpanded(group.type)) {
        return group.tasks;
      }
      return group.tasks.slice(0, this.defaultVisibleCount);
    },
    async previewTask(task) {
      if (task.content_id) {
        const toast = this.$createToast({
          time: 10000,
          mask: false,
        });
        toast.show();
        const res = await getContentById(task.content_id);
        if (res && res.value && res.value.length > 0) {
          const previewContent = res.value[0];
          this.htmlContent = previewContent.content_body;
          this.$store.dispatch('setDialogStatus', true);
        }
        toast.hide();
      }
    },
    ignoreTask(task) {
      Dialog.confirm({
        title: '确认操作',
        message: '确定要忽略该任务吗？',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
      })
        .then(() => {
          executeTask('ignore', task.campaign_event_id, task.physician_id, getLocalStorage("user_id"), localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', getLocalStorage('role_id') || '')
            .then(() => {
              this.$emit('ignoreTask', task);
              this.$toast('任务已忽略');
              this.$root.$emit('updateTaskCount');
            })
            .catch(error => {
              console.error('Failed to ignore task:', error);
              this.$toast('忽略任务失败，请稍后再试');
            });
        })
        .catch(() => {
          // 用户取消，不做处理
        });
    },
    async sendTask(task) {
      const toast = this.$createToast({
        time: 10000,
        mask: false,
      });
      toast.show();
      const res = await getContentById(task.content_id);
      if (res && res.value && res.value.length > 0) {
        const forwardContent = res.value[0];

        try {
          if (forwardContent.recommended_wording) {
            try {
              const wordingToCopy = await selectRecommendedWording(forwardContent.recommended_wording, {
                title: '建议话术',
                confirmButtonText: '复制话术',
                cancelButtonText: '跳过',
              });

              if (wordingToCopy) {
                const success = await wecomSDK.copyToClipboard(wordingToCopy);
                if (!success) {
                  this.$toast.fail('复制失败');
                }
              }
            } catch (err) {
              console.log(err);
            }
          }
          toast.show();

          const success = await wecomSDK.shareWechatMessage({
            content_id: task.content_id,
            sender_user_id: getLocalStorage("user_id"),
            sender_user_name: localStorage.getItem('user_name'),
            sender_territory_id: localStorage.getItem('territory_id'),
            receiver_physician_id: task.physician_id,
            send_time: new Date(),
            title: forwardContent.content_name,
            description: forwardContent.description,
            wx_logo: forwardContent.wx_logo,
          });

          if (success) {
            if (task.campaign_event_execution_id) {
              reExecuteTask(task.campaign_event_execution_id, 'accept', task.campaign_event_id, task.physician_id, getLocalStorage("user_id"), localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', getLocalStorage('role_id') || '')
                .then(() => {
                  this.$emit('sendTask', task);
                  this.$toast('任务已重新发送');
                  this.$root.$emit('updateTaskCount');
                })
                .catch(error => {
                  console.error('Failed to re-execute task:', error);
                  this.$toast('重新发送任务失败，请稍后再试');
                });
            } else {
              executeTask('accept', task.campaign_event_id, task.physician_id, getLocalStorage("user_id"), localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', getLocalStorage('role_id') || '')
                .then(() => {
                  this.$emit('sendTask', task);
                  this.$toast('任务已执行');
                  this.$root.$emit('updateTaskCount');
                })
                .catch(error => {
                  console.error('Failed to accept task:', error);
                  this.$toast('发送内容失败，请稍后再试');
                });
            }
          }
          toast.hide();
        } catch (err) {
          toast.hide();
          console.error('Forward error:', err);
          this.$toast.fail('转发失败，请稍后重试');
        }
      } else {
        toast.hide();
      }
    },
    closePreviewFn() {
      this.$store.dispatch('setDialogStatus', false);
      this.htmlContent = null;
    },
    // 新增：处理问卷任务
    openTypingDialog(task) {
      this.typingDialogTask = task;
      this.typingDialogAnswers = null; // 每次打开都清空
      this.showTypingDialog = true;
    },
    async onTypingFinish({ resultType, answers }) {
      // 问卷完成后，自动将任务标记为已执行
      if (this.typingDialogTask) {
        await this.executeSegmentTask(this.typingDialogTask);
        this.showTypingDialog = false;
        this.typingDialogTask = null;
        this.typingDialogAnswers = null;
        // 通知父组件刷新任务列表
        this.$emit('refreshTasks');
        // 通知更上层页面刷新分型
        this.$emit('refreshTyping');
      }
    },
    async executeSegmentTask(task) {
      try {
        await executeTask('accept', task.campaign_event_id, task.physician_id, getLocalStorage("user_id"), localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', getLocalStorage('role_id') || '');
        this.$toast('任务已执行');
        this.$root.$emit('updateTaskCount');
      } catch (error) {
        this.$toast('任务执行失败，请稍后再试');
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.task-list {
  text-align: left;;

  .task-group {
    margin-bottom: 14px;
  }

  .task-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding: 0 2px;
  }

  .task-group-title {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .task-group-name {
    font-size: 15px;
    font-weight: 600;
    color: #2c3034;
  }

  .task-group-count {
    min-width: 20px;
    height: 20px;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 10px;
    background: #f8e9e5;
    color: #aa341b;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    box-sizing: border-box;
  }

  .task-group-toggle {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: #aa341b;
    font-size: 13px;
    line-height: 20px;
    padding: 0 2px;
  }

  .task-item {
    background: #fff;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: 16px 12px;

    .task-row {
      display: flex;
      align-items: flex-start;
    }

    .left-column {
      margin-right: 8px;

      .thumb {
        width: 72px;
        img {
          width: 100%;
          border: 1px solid #f0f0f0;
          border-radius: 4px;
        }
      }

      .task-type {
          margin-bottom: 8px;
          // display: inline-block;
          padding: 4px 6px;
          border-radius: 6px;
          font-size: 10px;
          // width: 48px;
          text-align: center;
          flex-shrink: 0;
          font-weight: 600;
          border: rgba(#aa341b, 1) solid 2px;
          color: #aa341b;
      }
    }

    .task-content {
      flex: 1;
      margin-right: 5px;

      .title-wrapper {
        margin-bottom: 12px;

        .task-type {
          margin-bottom: 8px;
          // display: inline-block;
          padding: 4px 6px;
          border-radius: 4px;
          font-size: 10px;
          width: 48px;
          text-align: center;
          flex-shrink: 0;
          border: rgba(#aa341b, 1) solid 2px;
          color: #aa341b;
        }

        .task-title {
          font-size: 16px;
          font-weight: 500;
          color: #2c3034;
          line-height: 1.4;
        }
      }

      .user-info {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          margin-right: 8px;
        }

        .user-name {
          font-size: 17px;
          color: #2f3336;
          margin-right: 8px;
        }

        .weixin {
          width: 16px;
          height: 16px;
        }
      }

      .task-meta {
        font-size: 12px;
        color: #666;
        line-height: 1.6;

        p {
          margin: 0;
        }
      }
    }

    .action-buttons {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 8px;

      img {
        width: 26px;
        cursor: pointer;
      }
    }
  }
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}
.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}
.html-preview {
  padding: 0;
  max-width: 100%;
  overflow: auto;
  ::v-deep img {
    width: 100%;
    height: auto;
    display: block;
  }
}
</style>
